import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LinkIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { useTikTok } from '../context/TikTokContext';
import { useInstagram } from '../context/InstagramContext';
import { useFacebook } from '../context/FacebookContext';
import { useThreads } from '../context/ThreadsContext';
import { useTwitter } from '../context/TwitterContext';
import {
  Badge, Button, Card, Container, Empty, EngagementRate, Eyebrow, HistoryNote,
  Input, LiftBadge, Page, PageHeader, PlatformLogo, PostCard, RefreshControl,
  ScoreBadge, SectionTitle, Skeleton, SkeletonText, Subtitle, Title, ViewToggle,
} from '@monorepo/common/components/ui';
import { usePersistentState } from '@monorepo/common/hooks/usePersistentState';
import AccountsTable from '../components/AccountsTable';
import { SORTS, SORT_VALUES, sortAccounts } from '../components/AccountsSort';
import {
  compact, pct, platformLabel, platformRoute, signed,
} from '@monorepo/common/utils/platforms';

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

/**
 * Per-platform description of an account card.
 *
 * These five cards used to be five near-identical components, ~65 lines each,
 * which meant every layout fix had to be made five times and inevitably was
 * not. They differ only in which fields they read and what the badge says, so
 * that is all this table holds; one component below renders all of them.
 *
 * `stats` is ordered and rendered as-is, so a platform that exposes two
 * metrics gets two columns and one that exposes three gets three.
 *
 * Note there is no per-platform badge colour. The badge is chrome, and chrome
 * stays monochrome here so the account itself — its avatar, its numbers — is
 * what carries colour. The brand tokens are for /connect, where choosing a
 * platform is the actual task.
 */
const PLATFORMS = {
  tiktok: {
    label: 'TikTok',
    route: (a) => `/account/${a.id}`,
    name: (a) => a.display_name,
    handle: (a) => `@${a.username || a.open_id}`,
    avatar: (a) => a.avatar_url_100 || a.avatar_url,
    verified: (a) => a.is_verified,
    stats: [
      ['follower_count', 'Followers'],
      ['likes_count', 'Likes'],
      ['video_count', 'Videos'],
    ],
  },
  instagram: {
    label: 'Instagram',
    route: (a) => `/ig-account/${a.id}`,
    name: (a) => a.display_name || a.instagram_username,
    handle: (a) => `@${a.instagram_username}`,
    avatar: (a) => a.profile_picture_url,
    // Which metrics this account has. Stated as a positive on the richer one
    // rather than a warning on the other: an Instagram-Login connection is a
    // smaller feature set, not a fault, and a warning on a working account
    // reads as something to go fix.
    extraBadge: (a) => (a.metrics_access === 'full'
      ? { label: 'Full analytics', tone: 'success',
          title: 'Connected through Facebook — includes per-post reach, impressions and saves' }
      : null),
    stats: [
      ['followers_count', 'Followers'],
      ['follows_count', 'Following'],
      ['media_count', 'Posts'],
    ],
  },
  facebook: {
    label: 'Facebook',
    route: (a) => `/fb-page/${a.id}`,
    name: (a) => a.page_name,
    handle: (a) => a.page_category,
    avatar: (a) => a.profile_picture_url,
    stats: [
      ['followers_count', 'Followers'],
      ['fan_count', 'Page Likes'],
    ],
  },
  threads: {
    label: 'Threads',
    route: (a) => `/threads-account/${a.id}`,
    name: (a) => a.display_name || a.threads_username,
    handle: (a) => `@${a.threads_username}`,
    avatar: (a) => a.profile_picture_url,
    stats: [
      ['followers_count', 'Followers'],
      ['media_count', 'Posts'],
    ],
  },
  twitter: {
    label: 'X',
    route: (a) => `/twitter-account/${a.id}`,
    name: (a) => a.display_name || a.username,
    handle: (a) => `@${a.username}`,
    avatar: (a) => a.profile_image_url,
    stats: [
      ['followers_count', 'Followers'],
      ['tweet_count', 'Tweets'],
    ],
  },
};

const initial = (s) => (s || '?').trim().charAt(0).toUpperCase() || '?';

/**
 * One connected account: who it is, how big, which way it is moving, and
 * whether any of it is working.
 *
 * ### This used to be two cards, stacked
 *
 * The page rendered the same accounts TWICE — a "Growth" list built from
 * `/social_stats/summary` and, below it, a connected-accounts list built from
 * the five platform contexts. Neither said what it was, so the honest reading
 * of "what is the growth thing above?" is that nobody could tell them apart,
 * because they were the same accounts.
 *
 * They are one card now, and the join goes the right way round: the list is
 * the accounts you have CONNECTED, with performance merged in where it
 * exists. Driving it from the summary instead would silently drop an account
 * connected five minutes ago — it has no stats row until the collector runs,
 * and an account missing from the only list of accounts reads as a failed
 * connect.
 *
 * Follower count answers "how big", the delta answers "which way", and net
 * engagement plus the score answer "is any of it working" — an account can add
 * followers steadily while its posts get progressively less interesting, and
 * the follower line alone would never show it.
 */
const AccountRow = ({ account: a, busy, onRefresh }) => {
  const dir =
    a.followers_delta === null || a.followers_delta === undefined
      ? null
      : a.followers_delta > 0 ? 'up' : a.followers_delta < 0 ? 'down' : 'flat';

  return (
    <Card className="min-w-0">
      {/* Identity and score on one line. */}
      <div className="flex min-w-0 items-center gap-2">
        <Link
          to={a.route}
          className="flex min-h-[var(--tap-target)] min-w-0 flex-1 items-center gap-2 text-ink hover:underline sm:min-h-0"
        >
          {a.avatar ? (
            <img src={a.avatar} alt="" loading="lazy"
                 className="size-7 shrink-0 rounded-pill bg-surface-sunken object-cover" />
          ) : (
            <span className="flex size-7 shrink-0 items-center justify-center rounded-pill bg-surface-overlay text-caption font-medium text-ink">
              {initial(a.name)}
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 items-center gap-1">
              <span className="truncate text-body-sm font-medium">{a.name}</span>
              {a.verified && (
                <CheckBadgeIcon className="size-3.5 shrink-0 text-info" aria-label="Verified" />
              )}
            </span>
            {a.handle && <span className="block truncate text-caption text-ink-subtle">{a.handle}</span>}
          </span>
          {/* The mark instead of the coloured dot it replaced. A dot is a
              legend you have to learn; this is the icon already on their
              phone. Monochrome, per the design system — the label beside it
              is still what carries identity for anyone who cannot see it. */}
          <PlatformLogo platform={a.platform} label={platformLabel(a.platform)}
                        className="size-3.5 shrink-0 text-ink-subtle" />
        </Link>
        <ScoreBadge score={a.score} />
      </div>

      {a.extraBadge && (
        <Badge tone={a.extraBadge.tone} title={a.extraBadge.title} className="mt-2">
          {a.extraBadge.label}
        </Badge>
      )}

      {a.hasStats ? (
        <>
          {/* Followers, change, and the two performance figures on one row.
              Three numbers that are read together should sit together. */}
          <div className="mt-2 flex flex-wrap items-end gap-x-5 gap-y-2">
            <div className="min-w-0">
              <div className="flex items-baseline gap-1.5">
                <span className="text-[26px] leading-none tracking-tight text-ink tabular-nums">
                  {compact(a.followers)}
                </span>
                {dir && (
                  <span className={[
                    'text-caption font-semibold tabular-nums',
                    dir === 'up' ? 'text-success' : dir === 'down' ? 'text-danger' : 'text-ink-subtle',
                  ].join(' ')}>
                    <span aria-hidden="true">{dir === 'up' ? '▴' : dir === 'down' ? '▾' : '–'}</span>
                    {pct(a.followers_pct) ?? '—'}
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-caption-sm uppercase tracking-[0.08em] text-ink-subtle">
                Followers
                {a.followers_delta !== null && a.followers_delta !== undefined
                  ? ` · ${signed(a.followers_delta)}`
                  : ''}
              </div>
            </div>

            <div className="min-w-0">
              <div className="text-body-sm leading-none text-ink">
                <EngagementRate rate={a.net_engagement_rate} />
              </div>
              <div className="mt-1 text-caption-sm uppercase tracking-[0.08em] text-ink-subtle">
                Net eng.
              </div>
            </div>

            <div className="min-w-0">
              {/* ZERO IS AN ANSWER. This read `count > 0 ? count : '—'`, so an
                  account with nothing breaking out was shown the same em dash
                  as an account we know nothing about — and those are different
                  facts. `null` is unknown; 0 is "we looked, and none". */}
              <div className="text-body-sm leading-none tabular-nums text-ink">
                {a.breaking_out_count ?? '—'}
              </div>
              <div className="mt-1 text-caption-sm uppercase tracking-[0.08em] text-ink-subtle">
                Breaking out
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-line pt-2.5">
            <HistoryNote history={a.history} postsCounted={a.posts_counted} />
            <RefreshControl
              lastRefreshedAt={a.last_refreshed_at}
              stale={a.stale}
              cooldown={a.refresh_cooldown}
              busy={busy}
              onRefresh={onRefresh}
              className="ml-auto"
            />
          </div>
        </>
      ) : (
        // Connected, but the collector has not reached it yet. Said plainly:
        // a card that just stops after the name looks like a broken account
        // rather than a new one.
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-line pt-2.5">
          <p className="text-caption text-ink-subtle">
            No performance data yet — it appears after the next daily snapshot.
          </p>
          <RefreshControl
            lastRefreshedAt={null}
            stale={false}
            cooldown={a.refresh_cooldown}
            busy={busy}
            onRefresh={onRefresh}
            className="ml-auto"
          />
        </div>
      )}
    </Card>
  );
};

/** What a row looks like before its request has come back. */
const AccountRowSkeleton = () => (
  <Card className="min-w-0">
    <div className="flex items-center gap-2">
      <Skeleton className="size-7 rounded-pill" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <SkeletonText w="w-1/2" />
        <SkeletonText w="w-1/3" />
      </div>
      <Skeleton className="h-5 w-9 rounded-pill" />
    </div>
    <div className="mt-3 flex gap-5">
      <Skeleton className="h-7 w-20" />
      <Skeleton className="h-7 w-12" />
      <Skeleton className="h-7 w-10" />
    </div>
  </Card>
);

const Dashboard = () => {
  // One request for growth and standouts across every platform. The rule for
  // "what counts as notable" lives in the backend, not here — see
  // social_stats/scoring.py.
  const [summary, setSummary] = useState(null);
  // Remembered per browser: how someone wants to look at their own accounts is
  // a preference, not data, and re-picking it on every visit is the kind of
  // small friction nobody reports and everybody feels. Guarded against a value
  // from an older build -- an unknown sort key would order by nothing.
  const [sort, setSort] = usePersistentState('os:accounts-sort', 'followers', SORT_VALUES);
  const [view, setView] = usePersistentState('os:accounts-view', 'cards', ['cards', 'table']);
  const [summaryError, setSummaryError] = useState(false);
  const [refreshing, setRefreshing] = useState(null);

  const loadSummary = () =>
    fetch(`${API_BASE_URL}/social_stats/summary?days=7`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))));

  /**
   * Pull one account fresh from its platform.
   *
   * Per account, not "refresh all": on an org with a dozen accounts that
   * button is sixty-odd platform API calls behind one tap, against limits
   * shared with production, and the person almost always meant the account
   * they were looking at.
   */
  const refreshAccount = async (a) => {
    const key = `${a.platform}:${a.account_id}`;
    setRefreshing(key);
    try {
      const res = await fetch(`${API_BASE_URL}/social_stats/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform: a.platform, account_id: a.account_id }),
      });
      if (res.status === 429) {
        // Server says too soon. Take its countdown rather than guessing.
        const { retry_after: retryAfter } = await res.json();
        setSummary((prev) => prev && ({
          ...prev,
          accounts: prev.accounts.map((x) =>
            x.account_id === a.account_id && x.platform === a.platform
              ? { ...x, refresh_cooldown: retryAfter }
              : x),
        }));
        return;
      }
      // Re-read the whole summary: a refresh changes the standout lists too,
      // not just the one account's numbers.
      setSummary(await loadSummary());
    } catch {
      // Leave the existing figures on screen — a failed refresh should not
      // blank out data that is merely a few hours old.
    } finally {
      setRefreshing(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    loadSummary()
      .then((d) => !cancelled && setSummary(d))
      // Stats are additive to this page: if they fail, the account list is
      // still the point of the screen, so fail quietly rather than blanking it.
      .catch(() => !cancelled && setSummaryError(true));
    return () => { cancelled = true; };
  }, []);

  const { accounts: tiktokAccounts, loading: tiktokLoading } = useTikTok();
  const { accounts: instagramAccounts, loading: instagramLoading } = useInstagram();
  const { accounts: facebookAccounts, loading: facebookLoading } = useFacebook();
  const { accounts: threadsAccounts, loading: threadsLoading } = useThreads();
  const { accounts: twitterAccounts, loading: twitterLoading } = useTwitter();

  // Every context starts `loading: true` with an empty array, so "no accounts"
  // is the state this page is ALWAYS in for the first moment of its life.
  // Rendering the empty state from `accounts.length === 0` alone therefore
  // told everyone they had nothing connected, every single load, until the
  // five requests came back. An empty state is a claim; this is the condition
  // under which it is true.
  const accountsLoading =
    tiktokLoading || instagramLoading || facebookLoading || threadsLoading || twitterLoading;

  /**
   * The accounts you have connected, with performance merged in by key.
   *
   * Disconnect is deliberately NOT on these cards: it lives on each account's
   * own overview page. A destructive action inside a card whose whole surface
   * navigates is one mis-tap from gone, and it cost every card a 44px row plus
   * its margin.
   */
  const rows = useMemo(() => {
    const byKey = new Map(
      (summary?.accounts || []).map((s) => [`${s.platform}:${s.account_id}`, s])
    );
    const groups = [
      ['tiktok', tiktokAccounts],
      ['instagram', instagramAccounts],
      ['facebook', facebookAccounts],
      ['threads', threadsAccounts],
      ['twitter', twitterAccounts],
    ];
    const seen = new Set();
    const connected = groups.flatMap(([platform, list]) => (list || []).map((account) => {
      const cfg = PLATFORMS[platform];
      const key = `${platform}:${account.id}`;
      const stats = byKey.get(key);
      seen.add(key);
      const handle = cfg.handle(account);
      return {
        // Stats first so identity cannot be overwritten by a summary field of
        // the same name, and so `sortAccounts` and `AccountsTable` keep seeing
        // exactly the field names they already read.
        ...(stats || {}),
        platform,
        account_id: account.id,
        name: cfg.name(account) || 'Untitled account',
        handle,
        // The sort's `name` option orders on `username`; without this fallback
        // an account with no stats row yet would sort as unknown and go last
        // in a list ordered by name, which is the one ordering where every
        // account has a value.
        username: stats?.username || (handle || '').replace(/^@/, ''),
        avatar: cfg.avatar(account),
        verified: !!cfg.verified?.(account),
        extraBadge: cfg.extraBadge?.(account) || null,
        route: cfg.route(account),
        hasStats: !!stats,
      };
    }));

    // Anything the summary knows about that the connected-account lists did
    // not produce. A UNION rather than a join, because either source can be
    // empty on its own: one failing `/tiktok/accounts` would otherwise blank
    // out accounts we have a week of statistics for, which is a worse lie than
    // showing one without its avatar.
    const orphans = (summary?.accounts || [])
      .filter((s) => !seen.has(`${s.platform}:${s.account_id}`))
      .map((s) => ({
        ...s,
        name: s.username ? `@${s.username}` : platformLabel(s.platform),
        handle: null,
        avatar: null,
        verified: false,
        extraBadge: null,
        route: platformRoute(s.platform, s.account_id),
        hasStats: true,
      }));

    return [...connected, ...orphans];
  }, [summary, tiktokAccounts, instagramAccounts, facebookAccounts, threadsAccounts, twitterAccounts]);

  // Memoised because sortAccounts copies the array: without this every render
  // hands both views a new array and re-renders every card for nothing.
  const orderedAccounts = useMemo(() => sortAccounts(rows, sort), [rows, sort]);

  return (
    <Page>
      <Container>
        <PageHeader>
          <div className="min-w-0">
            <Eyebrow>
              <LinkIcon aria-hidden="true" />
              Accounts
            </Eyebrow>
            <Title>Connected Accounts</Title>
            <Subtitle>Manage the social accounts OddSignals publishes to.</Subtitle>
          </div>
          {/* Making a post is what this app is FOR, so it is the primary
              action here rather than a sidebar destination — an action listed
              among places reads as somewhere to go. Connecting an account is
              a setup step you do once and then rarely, so it drops to
              secondary; the empty state below covers having none at all.

              Both keep a short label below `sm` so the header stays one row
              on a phone. */}
          <div className="flex shrink-0 items-center gap-2">
            <Button as={Link} to="/connect" variant="secondary" size="sm" className="sm:h-auto sm:text-body-sm">
              <span className="sm:hidden">Connect</span>
              <span className="hidden sm:inline">Connect account</span>
            </Button>
            <Button as={Link} to="/compose" variant="primary" size="sm" className="sm:h-auto sm:text-body-sm">
              <span className="sm:hidden">Post</span>
              <span className="hidden sm:inline">Create post</span>
            </Button>
          </div>
        </PageHeader>

        {/* ── The accounts ───────────────────────────────────────────────
            Titled for what it holds. It was "Growth", above a second list of
            the same accounts with no title at all, which is how you end up
            being asked what the growth thing is. */}
        {(accountsLoading || rows.length > 0) && (
          <section className="mb-6 sm:mb-9">
            <div className="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 sm:mb-3">
              <div className="flex min-w-0 items-baseline gap-2">
                <SectionTitle className="text-body sm:text-h5">Your accounts</SectionTitle>
                <span className="truncate text-caption text-ink-subtle">
                  {summary?.days ? `Last ${summary.days} days` : 'Last 7 days'}
                  {/* A net change of exactly zero is a real answer and used to
                      be hidden, because `totals.delta ? … : ''` cannot tell 0
                      from absent. */}
                  {summary?.totals?.delta != null
                    ? ` · ${signed(summary.totals.delta)} followers`
                    : ''}
                </span>
              </div>
              {/* Sorting and layout are one group because they answer the same
                  question — "how do I want to look at these" — and splitting
                  them across the header sends your eye to two places.
                  `ml-auto` rather than the row's `justify-between`: when this
                  wraps to its own line on a phone, space-between has nothing
                  to push against and the controls land on the LEFT, under the
                  title. This keeps them right whether they wrap or not.

                  Rendered while loading too, disabled. They used to appear
                  only once the summary arrived, so the header visibly grew a
                  row a second after the page drew. */}
              <div className="ml-auto flex shrink-0 items-center gap-2">
                <label className="sr-only" htmlFor="accounts-sort">Sort accounts by</label>
                <Input
                  as="select"
                  id="accounts-sort"
                  value={sort}
                  disabled={accountsLoading}
                  onChange={(e) => setSort(e.target.value)}
                  className="w-auto py-1.5 pl-3 pr-2 text-caption sm:text-body-sm"
                >
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Input>
                <ViewToggle value={view} onChange={setView} />
              </div>
            </div>
            {accountsLoading ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
                <span className="sr-only" role="status">Loading your accounts</span>
                {[0, 1, 2].map((i) => <AccountRowSkeleton key={i} />)}
              </div>
            ) : view === 'table' ? (
              <AccountsTable accounts={orderedAccounts} sort={sort} onSort={setSort} />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {orderedAccounts.map((a) => (
                  <AccountRow
                    key={`${a.platform}-${a.account_id}`}
                    account={a}
                    busy={refreshing === `${a.platform}:${a.account_id}`}
                    onRefresh={() => refreshAccount(a)}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── Standouts ──────────────────────────────────────────────────
            Two lists, deliberately. "Breaking out" is unusual FOR THIS
            ACCOUNT; "Strongest posts" is good in absolute terms. A post can
            be in both, and the overlap is the interesting part. */}
        {(summary?.breaking_out?.length > 0 || summary?.top_scoring?.length > 0) && (
          <section className="mb-9 grid gap-3 lg:grid-cols-2">
            {summary.breaking_out?.length > 0 && (
              <Card className="min-w-0">
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <h3 className="text-body font-medium text-ink">Breaking out</h3>
                  <Link to="/posts?tier=breaking_out"
                        className="inline-flex min-h-[var(--tap-target)] items-center text-caption text-ink-muted hover:text-ink sm:min-h-0">View all</Link>
                </div>
                <p className="mb-2 text-caption text-ink-subtle">
                  Beating this account&rsquo;s own recent median.
                </p>
                <div className="-mx-2">
                  {summary.breaking_out.map((post) => (
                    <PostCard
                      compact
                      key={`${post.platform}-${post.post_id}`}
                      post={post}
                      badge={<LiftBadge lift={post.lift} tier={post.lift_tier} compactLabel />}
                    />
                  ))}
                </div>
              </Card>
            )}

            {summary.top_scoring?.length > 0 && (
              <Card className="min-w-0">
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <h3 className="text-body font-medium text-ink">Strongest posts</h3>
                  <Link to="/posts?sort=score"
                        className="inline-flex min-h-[var(--tap-target)] items-center text-caption text-ink-muted hover:text-ink sm:min-h-0">View all</Link>
                </div>
                <p className="mb-2 text-caption text-ink-subtle">
                  Engagement × reach, scored out of 10.
                </p>
                <div className="-mx-2">
                  {summary.top_scoring.map((post) => (
                    <PostCard
                      compact
                      key={`${post.platform}-${post.post_id}`}
                      post={post}
                      badge={<ScoreBadge score={post.score} />}
                    />
                  ))}
                </div>
              </Card>
            )}
          </section>
        )}

        {/* Only once every context has actually answered. Before this it
            rendered from `accounts.length === 0`, which is true for the whole
            first moment of every visit — so everyone was told they had nothing
            connected, on every load, including the people with ten accounts. */}
        {!accountsLoading && rows.length === 0 && (
          <Empty
            title="No accounts connected"
            icon={<LinkIcon className="h-8 w-8 text-ink-subtle" aria-hidden="true" />}
          >
            <p className="max-w-sm text-body-sm">
              Connect a social account to start publishing and tracking performance.
            </p>
            <Button as={Link} to="/connect" variant="primary" className="mt-2">
              Connect account
            </Button>
          </Empty>
        )}

        {rows.length > 0 && (
          <section className="mt-10">
            <SectionTitle className="mb-4">Quick actions</SectionTitle>
            <div className="grid gap-3 sm:grid-cols-2">
              <Card as={Link} to="/media/video-studio" interactive className="block text-left">
                <h3 className="text-body font-medium text-ink">Post a video</h3>
                <p className="mt-1 text-body-sm text-ink-muted">
                  Edit and publish to your connected accounts.
                </p>
              </Card>
              <Card as={Link} to="/posts" interactive className="block text-left">
                <h3 className="text-body font-medium text-ink">All posts</h3>
                <p className="mt-1 text-body-sm text-ink-muted">
                  Every post across every account, in one timeline.
                </p>
              </Card>
            </div>
          </section>
        )}
      </Container>
    </Page>
  );
};

export default Dashboard;
